/**
 * The one home of the cover-style genre-matching rule: sorts a story's cover style options so
 * ones that suit the story's genre come first, and reports which keys matched so the gallery can
 * tag them "Suits your genres". The server returns styles in plain position order and does not
 * sort — matching lives here because the gallery needs `suitedKeys` as well as the order.
 */

/**
 * Builds the token set used to match a story's genre string against a style's suited genres:
 * each comma-segment kept whole (so multi-word suited genres like "sci-fi" survive intact),
 * each whitespace-separated word within a segment, and each of those further split on any
 * non-alphanumeric character (so "sci-fi" also yields "sci" and "fi"). All lowercased.
 */
function genreWordSet(raw: string): Set<string> {
  const words = new Set<string>()
  for (const segment of raw.toLowerCase().split(',')) {
    const seg = segment.trim()
    if (!seg) continue
    words.add(seg)
    for (const token of seg.split(/\s+/)) {
      if (!token) continue
      words.add(token)
      for (const sub of token.split(/[^a-z0-9]+/)) {
        if (sub) words.add(sub)
      }
    }
  }
  return words
}

export function sortCoverStyles<T extends { key: string; suitedGenres: string[]; position: number }>(
  styles: T[],
  genre: string | null | undefined
): { sorted: T[]; suitedKeys: Set<string> } {
  const raw = (genre ?? '').trim().toLowerCase()
  const words = genreWordSet(raw)
  const suits = (s: T) => words.size > 0 && s.suitedGenres.some((g) => {
    const suited = g.trim().toLowerCase()
    if (!suited) return false
    // Multi-word suited genres (e.g. "science fiction") match as an exact phrase within the
    // genre string; single-word (or hyphenated) ones must equal a whole token, never a substring.
    if (suited.includes(' ')) return raw.includes(suited)
    return words.has(suited)
  })
  const suitedKeys = new Set<string>()
  const sorted = [...styles].sort((a, b) => {
    const sa = suits(a) ? 0 : 1
    const sb = suits(b) ? 0 : 1
    return sa - sb || a.position - b.position
  })
  for (const style of sorted) {
    if (suits(style)) suitedKeys.add(style.key)
  }
  return { sorted, suitedKeys }
}
